import React, { useState, useEffect, useMemo } from "react";
import { SidebarMenu } from "components";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    IconButton,
    Collapse,
    Avatar,
    Paper,
    AppBar,
    Toolbar,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogContent,
    Divider,
    Stack,
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    Store as StoreIcon,
} from "@mui/icons-material";

type Product = {
    id: string;
    name: string;
    brand?: string;
};

type OptionEntry = {
    productId: string;
    ok: boolean;
    comment?: string;
    photoUrl?: string | null;
    idealPrice?: number | null;
    salePrice?: number | null;
};

const mockProducts: Product[] = [
    { id: "p1", name: "Producto A", brand: "Marca Alpha" },
    { id: "p2", name: "Producto B", brand: "Marca Beta" },
    { id: "p3", name: "Producto C", brand: "Marca Gamma" },
];

const clientDefault = {
    CardCode: "CL02365",
    CardName: "GALO DE MENDOZA, ANA CRISTINA",
    CardType: "C",
    GroupCode: 104,
    CmpPrivate: "C",
    Address: "FINAL C GRIMALDI Y 12 AV NTE #1 SALIDA A SAN MIGUEL, C A STA MARIA, USULUTAN",
    ZipCode: null,
    MailAddres: null,
    MailZipCod: null,
    Phone1: "78344952",
    Phone2: null,
    Fax: null,
    CntctPrsn: "DUI 01002976 6",
    Notes: "SUPERMERCADO",
    Balance: 69.7,
    ChecksBal: 0,
    DNotesBal: 0,
    OrdersBal: 0,
    GroupNum: 5,
    CreditLine: 0,
    DebtLine: 0,
    Discount: null,
    AddID: "27073",
    CardFName: "SUPER GALO",
    CrCardNum: "/aJkEu9+xYoUrzxQe1zMCw==",
    DebPayAcct: "_SYS00000000014",
    U_TipoCont: "01",
    U_TipoSN: "CL",
    U_NIT: "11232109490015",
    U_Dia: null,
    U_HI: null,
    U_HF: null,
    U_canal: ["of-otrascadenas", "of-otrascadenas"],
    U_MMetas: null,
    U_seg_cli: "Supermercados",
    ListNum: 9,
    PymntGroup: "CONTADO",
    ExtraDays: 0,
    WTLiable: "Y",
    BPLLat: "13.3435184200072",
    BPLLon: "-88.4310021056706",
    L: "N",
    M: "N",
    X: "Y",
    J: "N",
    V: "N",
    S: "N",
    Frecuencia: "I",
    Semana: 48,
    TipoSemanaActual: "P",
    U_Ruta: "R003",
    BloqueoVentas: "Y",
    enabled: false,
    label: " CL02365 GALO DE MENDOZA, ANA CRISTINA SUPER GALO",
};

const makeReportEntries = (products: Product[]): {
    servicio: OptionEntry[];
    visibilidad: OptionEntry[];
    personal: OptionEntry[];
    precio: OptionEntry[];
    presencia: OptionEntry[];
    promociones: OptionEntry[];
} => {
    return {
        servicio: products.map((p, i) => ({
            productId: p.id,
            ok: i % 2 === 0,
            comment: i % 2 === 0 ? "Servicio correcto" : "Falta capacitación en turnos",
            photoUrl: null,
        })),
        visibilidad: products.map((p, i) => ({
            productId: p.id,
            ok: i % 2 !== 0,
            comment: i % 2 !== 0 ? "Producto visible en góndola" : "No aparece en góndola principal",
            photoUrl: i === 1 ? "https://via.placeholder.com/600x400?text=Visibilidad+" + p.id : null,
        })),
        personal: products.map((p, i) => ({
            productId: p.id,
            ok: true,
            comment: "Personal con buena atención",
            photoUrl: null,
        })),
        precio: products.map((p, i) => ({
            productId: p.id,
            ok: i % 2 === 0,
            comment: i % 2 === 0 ? "Precio competitivo" : "Precio por encima del ideal",
            idealPrice: 10 + i * 2,
            salePrice: 11 + i * 3,
        })),
        presencia: products.map((p, i) => ({
            productId: p.id,
            ok: i % 2 === 0,
            comment: i % 2 === 0 ? "Producto en menú" : "Sin foto en menú",
            photoUrl: i === 0 ? "https://via.placeholder.com/600x400?text=Presencia+" + p.id : null,
        })),
        promociones: products.map((p, i) => ({
            productId: p.id,
            ok: false,
            comment: "No se observan promociones activas",
            photoUrl: i === 2 ? "https://via.placeholder.com/600x400?text=Promociones+" + p.id : null,
        })),
    };
};

const OnSixRatingDetails: React.FC = () => {
    const navigate = useNavigate();
    const reports = makeReportEntries(mockProducts);

    const [brandFilter, setBrandFilter] = useState<string>("all");
    const brands = useMemo(() => {
        const setBrands = new Set<string>();
        mockProducts.forEach((p) => {
            if (p.brand) setBrands.add(p.brand);
        });
        return Array.from(setBrands).sort();
    }, []);

    const [globalComment, setGlobalComment] = useState<string | null>(null);

    const globalStats = useMemo(() => {
        const all = [
            ...reports.servicio,
            ...reports.visibilidad,
            ...reports.personal,
            ...reports.precio,
            ...reports.presencia,
            ...reports.promociones,
        ];
        const total = all.length;
        const okCount = all.filter((e) => e.ok).length;
        const score = total > 0 ? Math.round((okCount / total) * 50) / 10 : 0;
        return { total, okCount, score };
    }, [reports]);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("onsix_global_comment");
            if (raw && typeof raw === "string" && raw.trim().length > 0) {
                setGlobalComment(raw.trim().slice(0, 1000));
                return;
            }
        } catch (e) { }

        const allComments = [
            ...reports.servicio,
            ...reports.visibilidad,
            ...reports.personal,
            ...reports.precio,
            ...reports.presencia,
            ...reports.promociones,
        ]
            .map((r) => r.comment)
            .filter(Boolean);
        const fallback = allComments.length > 0 ? allComments.join(" · ").slice(0, 1000) : null;
        setGlobalComment(fallback);
    }, [reports]);

    const [client, setClient] = useState<typeof clientDefault>(clientDefault);
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("onsix_selected_client");
            if (raw) {
                setClient(JSON.parse(raw));
                return;
            }
        } catch (e) { }
        setClient(clientDefault);
    }, []);

    const [openIds, setOpenIds] = useState<string[]>([]);
    const toggleOpen = (id: string) =>
        setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const findEntry = (list: OptionEntry[], productId: string) =>
        list.find((e) => e.productId === productId) ?? ({ productId, ok: false } as OptionEntry);

    const [modalImage, setModalImage] = useState<string | null>(null);
    const openImage = (url: string | null | undefined) => {
        if (url) setModalImage(url);
    };
    const closeImage = () => setModalImage(null);

    const filteredProducts = useMemo(() => {
        if (brandFilter === "all") return mockProducts;
        return mockProducts.filter((p) => p.brand === brandFilter);
    }, [brandFilter]);

    type ReportRowProps = { title: string; entry: OptionEntry; onViewPhoto?: (url: string) => void };

    const ReportRow = ({ title, entry, onViewPhoto }: ReportRowProps) => (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {title}
                    </Typography>
                    <Chip
                        icon={entry.ok ? <CheckCircleIcon /> : <CancelIcon />}
                        label={entry.ok ? "Sí" : "No"}
                        color={entry.ok ? "success" : "error"}
                        size="small"
                    />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Comentario:</strong> {entry.comment ?? "—"}
                </Typography>

                {(entry.idealPrice != null || entry.salePrice != null) && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                            <strong>Precio ideal:</strong> {entry.idealPrice != null ? entry.idealPrice.toFixed(2) : "—"}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Precio venta:</strong> {entry.salePrice != null ? entry.salePrice.toFixed(2) : "—"}
                        </Typography>
                        {entry.idealPrice != null && entry.salePrice != null && (
                            <Typography variant="caption" color="text.secondary">
                                Diferencia: {(entry.salePrice - entry.idealPrice).toFixed(2)}
                            </Typography>
                        )}
                    </Box>
                )}

                {entry.photoUrl && (
                    <Stack direction="row" spacing={1} alignItems="center" mt={2}>
                        <Box
                            component="img"
                            src={entry.photoUrl}
                            alt="evidencia"
                            sx={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                borderRadius: 1,
                                cursor: "pointer",
                                border: 1,
                                borderColor: "divider",
                            }}
                            onClick={() => onViewPhoto && onViewPhoto(entry.photoUrl as string)}
                        />
                        {onViewPhoto && (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<VisibilityIcon />}
                                onClick={() => onViewPhoto(entry.photoUrl as string)}
                            >
                                Ver foto
                            </Button>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "grey.50" }}>
            {/* Sidebar */}
            <Box sx={{ width: 240, flexShrink: 0, borderRight: 1, borderColor: "divider", bgcolor: "white" }}>
                <SidebarMenu pageName="on6" />
            </Box>

            {/* Main content */}
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* AppBar */}
                <AppBar position="sticky" color="default" elevation={1}>
                    <Toolbar>
                        <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            On6 — Reporte por producto
                        </Typography>
                    </Toolbar>
                </AppBar>

                {/* Scrollable content */}
                <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
                    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
                        {/* Client info card */}
                        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                            <Stack direction="row" spacing={3} alignItems="flex-start">
                                <Avatar sx={{ width: 80, height: 80, bgcolor: "primary.main" }}>
                                    <StoreIcon fontSize="large" />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="start">
                                        <Box>
                                            <Typography variant="h6">{client.CardName}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {client.CardFName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Código: <strong>{client.CardCode}</strong>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: "right" }}>
                                            <Typography variant="body2">{client.PymntGroup}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Ruta: {client.U_Ruta}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider sx={{ my: 2 }} />

                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <Typography variant="body2">
                                                <strong>Teléfono:</strong> {client.Phone1 ?? "—"}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>NIT:</strong> {client.U_NIT ?? "—"}
                                            </Typography>
                                        </Grid>
                                        <Grid size={12}>
                                            <Typography variant="body2">
                                                <strong>Dirección:</strong> {client.Address ?? "—"}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Segmento:</strong> {client.U_seg_cli ?? "—"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Global stats */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={3}>
                                <Card sx={{ minHeight: 190 }}>
                                    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Calificación global
                                        </Typography>
                                        <Typography variant="h4" color="primary">
                                            {globalStats.score}/5
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Basado en {globalStats.total} checks — {globalStats.okCount} OK
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={9}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Comentario global
                                        </Typography>
                                        <Box sx={{ maxHeight: 120, overflow: "auto" }}>
                                            <Typography variant="body2">
                                                {globalComment || <em style={{ color: "#999" }}>Sin comentarios</em>}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                            Máx. 1000 caracteres
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Filter and products */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Productos: {filteredProducts.length}
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Filtrar marca</InputLabel>
                                <Select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} label="Filtrar marca">
                                    <MenuItem value="all">Todas</MenuItem>
                                    {brands.map((b) => (
                                        <MenuItem key={b} value={b}>
                                            {b}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Grid container spacing={3}>
                            {filteredProducts.map((product) => {
                                const servicio = findEntry(reports.servicio, product.id);
                                const visibilidad = findEntry(reports.visibilidad, product.id);
                                const personal = findEntry(reports.personal, product.id);
                                const precio = findEntry(reports.precio, product.id);
                                const presencia = findEntry(reports.presencia, product.id);
                                const promociones = findEntry(reports.promociones, product.id);
                                const isOpen = openIds.includes(product.id);

                                return (
                                    <Grid size={12} key={product.id}>
                                        <Card elevation={3}>
                                            <CardContent sx={{ cursor: "pointer" }} onClick={() => toggleOpen(product.id)}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                        <Typography variant="h6">{product.name}</Typography>
                                                        {product.brand && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {product.brand}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            Resumen de opciones
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        sx={{
                                                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                            transition: "transform 0.3s",
                                                        }}
                                                    >
                                                        <ExpandMoreIcon />
                                                    </IconButton>
                                                </Stack>
                                            </CardContent>

                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                <CardContent sx={{ pt: 0 }}>
                                                    <ReportRow title="Servicio perfecto" entry={servicio} />
                                                    <ReportRow
                                                        title="Visibilidad perfecta"
                                                        entry={visibilidad}
                                                        onViewPhoto={(url) => openImage(url)}
                                                    />
                                                    <ReportRow title="Personal perfectamente capacitado" entry={personal} />
                                                    <ReportRow title="Precio perfecto" entry={precio} />
                                                    <ReportRow
                                                        title="Presencia en el menú"
                                                        entry={presencia}
                                                        onViewPhoto={(url) => openImage(url)}
                                                    />
                                                    <ReportRow
                                                        title="Promociones perfectas"
                                                        entry={promociones}
                                                        onViewPhoto={(url) => openImage(url)}
                                                    />
                                                </CardContent>
                                            </Collapse>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                </Box>
            </Box>

            {/* Image modal */}
            <Dialog open={!!modalImage} onClose={closeImage} maxWidth="lg" fullWidth>
                <DialogContent sx={{ position: "relative", p: 0 }}>
                    <IconButton
                        onClick={closeImage}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "white",
                            "&:hover": { bgcolor: "grey.200" },
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {modalImage && (
                        <Box component="img" src={modalImage} alt="evidencia ampliada" sx={{ width: "100%", display: "block" }} />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default OnSixRatingDetails;